import React, { useState } from "react";
import { API_BASE_URL } from "../utils/constants.js";

export default function MicroWellApi(
    parameters = {},
    rows = [],
    calibrationResponse = {},
    aluValue = 0
) {
    const [response, setResponse] = useState(null);
    const [predictResponse, setPredictResponse] = useState(null);

    const [loading, setLoading] = useState(false);
    const [predictLoading, setPredictLoading] = useState(false);

    const [error, setError] = useState(null);
    const [predictError, setPredictError] = useState(null);

    // =========================
    // CALIBRATION PAYLOAD
    // MicroWell calibration uses up to 3 cal levels (MAX_MW_LEVELS = 3),
    // each with 1..N ALU replicates. The MGCC curve (afMGCCConcentration /
    // afMGCCSignal) anchors the shape factor and is supplied separately from
    // the per-level raw ALU reps.
    // =========================
    const buildPayload = () => {
        const cleanRows = (rows || []).filter(r => r.conc !== "" && r.conc !== null && r.conc !== undefined);

        const afCalConcentration = cleanRows.map(r => Number(r.conc)).filter(v => !isNaN(v));

        // aafALU is a flattened 2-D array: [level0_rep0, level0_rep1, ..., level1_rep0, ...]
        // Each row's resp field can be a space/comma-separated list of replicates.
        const aafALU = [];
        const asNumberCalReps = [];

        cleanRows.forEach(r => {
            let reps = [];
            if (Array.isArray(r.resp)) {
                reps = r.resp.map(Number).filter(v => !isNaN(v));
            } else if (typeof r.resp === "string") {
                reps = r.resp.split(/[\s,]+/).filter(v => v !== "").map(Number).filter(v => !isNaN(v));
            } else if (r.resp !== "" && r.resp !== null && r.resp !== undefined) {
                reps = [Number(r.resp)];
            }
            asNumberCalReps.push(reps.length);
            aafALU.push(...reps);
        });

        // MGCC curve points (optional - supplied via parameters)
        const mgccConcs = (parameters?.afMGCCConcentration || []).map(Number).filter(v => !isNaN(v));
        const mgccSignals = (parameters?.afMGCCSignal || []).map(Number).filter(v => !isNaN(v));

        return {
            eAssayModelType: Number(parameters?.eAssayModelType || 0),
            eMwMathModel: Number(parameters?.eMwMathModel || 0),
            sNumberCalLevels: afCalConcentration.length,
            asNumberCalReps,
            afCalConcentration,
            aafALU,
            sNumMGCCPoints: mgccConcs.length,
            afMGCCConcentration: mgccConcs,
            afMGCCSignal: mgccSignals,
            fSQCutoff_A: Number(parameters?.fSQCutoff_A || 0),
            fSQCutoff_B: Number(parameters?.fSQCutoff_B || 0),
            fSQCutoff_C: Number(parameters?.fSQCutoff_C || 0),
            fSQCutoff_D: Number(parameters?.fSQCutoff_D || 0)
        };
    };

    // =========================
    // PREDICTION PAYLOAD
    // The predict call requires the raw ALU for the unknown sample together
    // with the full assay definition (cal params + MGCC curve) produced by
    // the calibration step.
    // =========================
    const buildPredictPayload = (singleAlu) => {
        const cal = calibrationResponse;
        return {
            input: {
                fALU: Number(singleAlu ?? aluValue ?? 0)
            },
            assay: {
                eAssayModelType: Number(cal?.eAssayModelType || 0),
                eMwMathModel: Number(cal?.eMwMathModel || 0),
                fSQCutoff_U: Number(cal?.fSQCutoff_U ?? cal?.fSQCutoff_A ?? 0),
                fSQCutoff_V: Number(cal?.fSQCutoff_V ?? cal?.fSQCutoff_B ?? 0),
                fSQCutoff_W: Number(cal?.fSQCutoff_W ?? cal?.fSQCutoff_C ?? 0),
                fSQCutoff_Z: Number(cal?.fSQCutoff_Z ?? cal?.fSQCutoff_D ?? 0),
                sNumberCalLevels: Number(cal?.sNumberCalLevels || 0),
                afCalConcentration: cal?.afCalConcentration || [],
                sNumMGCCPoints: Number(cal?.sNumMGCCPoints || 0),
                afMGCCConcentration: cal?.afMGCCConcentration || [],
                afMGCCSignal: cal?.afMGCCSignal || [],
                sMwCalParameterCount: Number(cal?.sMwCalParameterCount || 0),
                fMwCalParameter1: Number(cal?.fMwCalParameter1 || 0),
                fMwCalParameter2: Number(cal?.fMwCalParameter2 || 0),
                fMwCalParameter3: Number(cal?.fMwCalParameter3 || 0),
                fMwCalParameter4: Number(cal?.fMwCalParameter4 || 0),
                fMwCalParameter5: Number(cal?.fMwCalParameter5 || 0),
                fCalSQCutOff: Number(cal?.fCalSQCutOff || 0),
                afCalSignal: cal?.afCalSignal || []
            }
        };
    };

    // =========================
    // CALIBRATION API
    // =========================
    const callApi = async () => {
        const payload = buildPayload();
        console.log("MICROWELL CALIBRATION PAYLOAD:", payload);

        setLoading(true);
        setError(null);

        try {
            const res = await fetch(
                API_BASE_URL + "api/v1/microwell/calibrate",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload)
                }
            );
            const data = await res.json();
            setResponse(data);
            return data;
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    };

    // =========================
    // PREDICTION API
    // Accepts an array of prediction input items so the caller can batch
    // all unknowns in one call, mirroring the MicroTip pattern.
    // =========================
    const callPredictApi = async (predictionInput = []) => {
        setPredictLoading(true);
        setPredictError(null);

        try {
            console.log("MICROWELL PREDICT ALL INPUT:", predictionInput);
            const updatedResults = await Promise.all(
                predictionInput.map(async (item) => {
                    const payload = buildPredictPayload(item?.alu ?? item?.response ?? 0);
                    console.log("MICROWELL PREDICT PAYLOAD:", payload);

                    const res = await fetch(
                        API_BASE_URL + "api/v1/microwell/predict",
                        {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify(payload)
                        }
                    );
                    const data = await res.json();

                    return {
                        ...item,
                        concentration: data?.dConcentration ?? "",
                        conditionCode: data?.zConditionCode ?? "",
                        apiResponse: data
                    };
                })
            );

            setPredictResponse(updatedResults);
            return updatedResults;
        } catch (err) {
            setPredictError(err.message);
            return null;
        } finally {
            setPredictLoading(false);
        }
    };

    return {
        callApi,
        response,
        loading,
        error,

        callPredictApi,
        predictResponse,
        predictLoading,
        predictError
    };
}
